import { Link } from "react-router";
import { EmptyState } from "../ui/EmptyState";
import type { ConversationPreview } from "../../api/message.api";

interface ConversationListProps {
  conversations: ConversationPreview[];
}

export function ConversationList({ conversations }: ConversationListProps) {
  if (conversations.length === 0) {
    return (
      <EmptyState
        title="No conversations"
        description="Start a conversation from a business profile."
        action={{ label: "Explore businesses", href: "/explore" }}
      />
    );
  }

  return (
    <div className="divide-y divide-gray-100" role="list" aria-label="Conversations">
      {conversations.map((convo) => (
        <ConversationCard key={convo.id} conversation={convo} />
      ))}
    </div>
  );
}

function ConversationCard({ conversation }: { conversation: ConversationPreview }) {
  const { otherParticipant, lastMessage, unreadCount } = conversation;
  const name = otherParticipant?.name ?? "Unknown";
  const avatarUrl = otherParticipant?.avatarUrl;
  const initial = name.charAt(0).toUpperCase();

  const timeLabel = lastMessage
    ? formatRelativeTime(lastMessage.createdAt)
    : formatRelativeTime(conversation.createdAt);

  const preview = lastMessage?.body ?? "No messages yet";

  return (
    <Link
      to={`/messages/${conversation.id}`}
      role="listitem"
      aria-label={`Conversation with ${name}${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
    >
      {/* Avatar */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="h-12 w-12 shrink-0 rounded-full object-cover"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
          {initial}
        </div>
      )}

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`truncate text-sm ${unreadCount > 0 ? "font-semibold text-gray-900" : "font-medium text-gray-900"}`}>
            {name}
          </p>
          <span className="shrink-0 text-xs text-gray-500">{timeLabel}</span>
        </div>
        <p
          className={`mt-0.5 truncate text-sm ${
            unreadCount > 0 ? "font-medium text-gray-900" : "text-gray-500"
          }`}
        >
          {preview}
        </p>
      </div>

      {/* Unread badge */}
      {unreadCount > 0 && (
        <div className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-gray-900 px-1.5 text-[11px] font-semibold text-white">
          {unreadCount > 99 ? "99+" : unreadCount}
        </div>
      )}
    </Link>
  );
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}
